import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface Args {
  alertIds: readonly string[]
  onToggleSelect: (id: string, range: boolean) => void
  onToggleSelectAll: () => void
  onClearSelection: () => void
  onShowShortcuts: () => void
  onFocusSearch: () => void
}

/**
 * AF-126 — Queue-level keyboard navigation (j/k/Enter/space/x/?/Esc).
 *
 * Returns the current keyboard-focused row index. The table observes this
 * to render the focus ring and keep the row in view. Skips when the user
 * is typing in an input.
 */
export function useQueueKeyboard({
  alertIds,
  onToggleSelect,
  onToggleSelectAll,
  onClearSelection,
  onShowShortcuts,
  onFocusSearch,
}: Args) {
  const navigate = useNavigate()
  const [focusedIndex, setFocusedIndex] = useState(0)

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Don't hijack keys while the user is typing.
      const target = e.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
        if (e.key === 'Escape' && tag === 'input') {
          ;(e.target as HTMLInputElement).blur()
        }
        return
      }
      // Don't hijack Enter / Space when the user has tabbed onto an
      // interactive element (anchor, button, role=button, link). The
      // queue shortcut should only fire while the user is navigating
      // rows, not while activating the skip link, sidebar links, or
      // toolbar buttons.
      if (
        tag === 'a' ||
        tag === 'button' ||
        target?.getAttribute('role') === 'button' ||
        target?.getAttribute('role') === 'link'
      ) {
        return
      }

      if (e.key === 'j') {
        e.preventDefault()
        setFocusedIndex((i) => Math.min(alertIds.length - 1, i + 1))
        if (e.shiftKey) {
          const next = alertIds[Math.min(alertIds.length - 1, focusedIndex + 1)]
          if (next) onToggleSelect(next, true)
        }
      } else if (e.key === 'k') {
        e.preventDefault()
        setFocusedIndex((i) => Math.max(0, i - 1))
        if (e.shiftKey) {
          const next = alertIds[Math.max(0, focusedIndex - 1)]
          if (next) onToggleSelect(next, true)
        }
      } else if (e.key === 'Enter') {
        const id = alertIds[focusedIndex]
        if (id) {
          e.preventDefault()
          void navigate(`/alerts/${id}`)
        }
      } else if (e.key === ' ') {
        const id = alertIds[focusedIndex]
        if (id) {
          e.preventDefault()
          onToggleSelect(id, false)
        }
      } else if (e.key === 'x') {
        e.preventDefault()
        onToggleSelectAll()
      } else if (e.key === '/') {
        e.preventDefault()
        onFocusSearch()
      } else if (e.key === 'Escape') {
        onClearSelection()
      } else if (e.key === '?') {
        e.preventDefault()
        onShowShortcuts()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    alertIds,
    focusedIndex,
    navigate,
    onClearSelection,
    onFocusSearch,
    onShowShortcuts,
    onToggleSelect,
    onToggleSelectAll,
  ])

  // Clamp the focused index when the list shrinks — derived, no setState in effect.
  const clampedIndex = useMemo(() => {
    if (alertIds.length === 0) return 0
    return Math.min(focusedIndex, alertIds.length - 1)
  }, [alertIds.length, focusedIndex])

  return { focusedIndex: clampedIndex, setFocusedIndex }
}
